// useWhaleNotifier — Browser-Notifications bei neuen Whale-Alerts.
//
// Doc-Anforderung 2026-05-16: V3 hatte den Whale-Alarm noch nicht portiert.
// Mechanik:
//   - localStorage-Flag `eve_whale_notify_enabled` (per-User-Pref, persistent)
//   - Notification-API: Permission on-toggle anfragen, dabei nur an wenn
//     `granted`
//   - Subscriben auf `useWhaleAlertsStore`; bei jedem neuen Alert (head-Item
//     ID ändert sich), eine Notification feuern — aber nur wenn Tab nicht
//     im Vordergrund (document.visibilityState !== 'visible'), sonst nervig
//   - Anti-Spam: max 1 Notification/sec (per-tab Rate-Limit)
//
// Returnt: enabled · supported · permission · toggle().

import { useCallback, useEffect, useRef, useState } from 'react';
import { selectWhaleAlerts, useWhaleAlertsStore } from '@/stores/whale-alerts-store';
import type { WhaleAlert } from '@/types/hyperliquid';

const STORAGE_KEY = 'eve_whale_notify_enabled';
const RATE_LIMIT_MS = 1_000;

export interface UseWhaleNotifierReturn {
  /** Benutzer hat Alarm aktiviert UND Permission `granted`. */
  enabled: boolean;
  /** Browser unterstützt Notification API überhaupt. */
  supported: boolean;
  /** Aktueller Permission-State ('default' · 'granted' · 'denied'). */
  permission: NotificationPermission | 'unsupported';
  /** Toggle an/aus — beim Aktivieren wird Permission angefragt falls nötig. */
  toggle: () => void;
}

export function useWhaleNotifier(): UseWhaleNotifierReturn {
  const supported =
    typeof window !== 'undefined' && typeof window.Notification !== 'undefined';

  const [enabledFlag, setEnabledFlag] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });

  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    () => (supported ? Notification.permission : 'unsupported'),
  );

  const lastNotifiedKeyRef = useRef<string | null>(null);
  const lastNotifiedAtRef = useRef<number>(0);

  // enabled = Flag AN UND Permission GRANTED.
  const enabled = enabledFlag && permission === 'granted';

  const toggle = useCallback(() => {
    if (!supported) return;
    if (enabledFlag) {
      // Ausschalten: nur Flag flippen, Permission lassen wir.
      setEnabledFlag(false);
      window.localStorage.setItem(STORAGE_KEY, '0');
      return;
    }
    // Anschalten: Permission anfragen wenn nötig.
    if (Notification.permission === 'granted') {
      setPermission('granted');
      setEnabledFlag(true);
      window.localStorage.setItem(STORAGE_KEY, '1');
      return;
    }
    if (Notification.permission === 'denied') {
      // Browser hat blockiert — Hint via title-Attribut wird im UI gezeigt.
      setPermission('denied');
      return;
    }
    // 'default' → fragen.
    void Notification.requestPermission().then((result) => {
      setPermission(result);
      if (result === 'granted') {
        setEnabledFlag(true);
        window.localStorage.setItem(STORAGE_KEY, '1');
      }
    });
  }, [enabledFlag, supported]);

  // Subscribe direkt am Store damit wir nur dann notifizieren wenn neue
  // Alerts reinkommen — nicht bei jedem Re-Render. Frühes Bail wenn nicht
  // enabled. Notify nur wenn Tab im Hintergrund (visibility !== 'visible').
  useEffect(() => {
    if (!supported || !enabled) return;
    const unsub = useWhaleAlertsStore.subscribe((state) => {
      const head = selectWhaleAlerts(state)[0];
      if (!head) return;
      const key = whaleKey(head);
      if (key === lastNotifiedKeyRef.current) return;
      const now = Date.now();
      if (now - lastNotifiedAtRef.current < RATE_LIMIT_MS) return;
      lastNotifiedKeyRef.current = key;
      lastNotifiedAtRef.current = now;
      // Nur notifizieren wenn Tab im Hintergrund — sonst spamt es Doc beim
      // Lesen der Liste selbst.
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        return;
      }
      try {
        new Notification(`🐋 ${head.side} ${head.coin} · $${(head.sizeUsd / 1_000_000).toFixed(1)}M`, {
          body: head.wallet
            ? `Wallet ${shortAddr(head.wallet)} · klick für Details`
            : 'Klick für Details',
          icon: '/eve-satoshi.png',
          tag: `whale-${head.coin}`, // Same coin: replace statt stack
        });
      } catch (err) {
        console.warn('[whale-notifier] Notification failed:', err);
      }
    });
    return () => {
      unsub();
    };
  }, [enabled, supported]);

  return { enabled, supported, permission, toggle };
}

function whaleKey(a: WhaleAlert): string {
  return `${a.time}-${a.coin}-${a.wallet ?? a.hash ?? ''}`;
}

function shortAddr(addr: string | null): string {
  if (!addr || addr.length < 8) return '—';
  return `${addr.slice(0, 4)}…${addr.slice(-4)}`;
}
